/**
 * @file Renders an investor's name cell with note and warning indicators, optionally opening the investment drawer.
 */

import React, { useMemo } from "react";
import { Space, Tooltip, Typography } from "antd";
import { FileTextOutlined, WarningOutlined } from "@ant-design/icons";
import type { ReactNode } from "react";

import { W0 } from "@components/Constants";

import hasTin from "./helpers/hasTin";
import isTrustInvestment from "./helpers/isTrustInvestment";
import isSdiraInvestment from "./helpers/isSdiraInvestment";
import computeNonUsPersonWarning from "./helpers/computeNonUsPersonWarning";
import type { Investment } from "./helpers/types";

const { Link: TextLink } = Typography;

const LABEL_NO_TIN = "Doesn't have EIN / SSN.";
const LABEL_OTHER_CUSTODIAN = "Other custodian selected.";

const OTHER_CUSTODIAN_ID = "OTHER";
const WARNINGS_SEPARATOR = " ";

const ICON_SPACE_SIZE = W0 / 2;

type ColumnInvestment = Investment & {
  note?: string;
};

type Props = {
  name: ReactNode;
  isBold?: boolean;
  investment: ColumnInvestment;
  openInvestmentDrawer?: (investment: ColumnInvestment) => void;
};

/**
 * Renders the investor name with note and warning icons, linking to the investment drawer when one is given.
 */
const InvestorColumn = ({
  isBold = false,
  name,
  investment,
  openInvestmentDrawer,
}: Props): JSX.Element => {
  const [ hasWarning, warningTitle ] = useMemo((): [boolean, string] => {
    const {
      profileJoint = {},
      profileSdira = {},
      profileEntity = {},
      profileIndividual = {},
    } = investment;

    const {
      isUsPerson: entityIsUsPerson,
    } = profileEntity;

    const {
      isUsPerson: jointIsUsPerson,
    } = profileJoint;

    const {
      custodianId,
      isUsPerson: sdiraUsPerson,
    } = profileSdira;

    const {
      isUsPerson: individualUsPerson,
    } = profileIndividual;

    const isNonUsPerson =
      sdiraUsPerson === false ||
      jointIsUsPerson === false ||
      entityIsUsPerson === false ||
      individualUsPerson === false;

    const isSdira = isSdiraInvestment(investment);
    const isTrust = isTrustInvestment(investment);

    const hasNoTin = !hasTin(investment);

    const isTinRequired = !isSdira && !isTrust;

    const isTinMissing = isTinRequired && hasNoTin;

    const isCustodianOther = custodianId === OTHER_CUSTODIAN_ID;

    const warnings: string[] = [];

    if (isNonUsPerson) {
      const nonUsPersonWarning = computeNonUsPersonWarning(investment);

      warnings.push(nonUsPersonWarning);
    }

    if (isCustodianOther) {
      warnings.push(LABEL_OTHER_CUSTODIAN);
    }

    if (isTinMissing) {
      warnings.push(LABEL_NO_TIN);
    }

    const shouldShowWarning = warnings.length > 0;
    const warning = warnings.join(WARNINGS_SEPARATOR);

    return [ shouldShowWarning, warning ];
  }, [ investment ]);

  const { note } = investment;

  const hasNote = !!note;

  const content = (
    <Space size={ICON_SPACE_SIZE}>
      {
        hasNote && (
          <Tooltip
            title={note}
            placement="top"
          >
            <FileTextOutlined />
          </Tooltip>
        )
      }

      {
        hasWarning && (
          <Tooltip
            title={warningTitle}
            placement="top"
          >
            <WarningOutlined />
          </Tooltip>
        )
      }

      {
        isBold
          ? <strong>{name}</strong>
          : name
      }
    </Space>
  );

  const isStatic = !openInvestmentDrawer;

  if (isStatic) {
    return content;
  }

  /**
   * Opens the investment drawer for this row.
   */
  const onClick = (): void => openInvestmentDrawer(investment);

  return (
    <TextLink
      strong
      onClick={onClick}
    >
      {content}
    </TextLink>
  );
};

export default InvestorColumn;
